<script>
	import CustomerReview from '$lib/Components/CustomerReview/CustomerReview.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import acrylicInsertLargeWindows from './images/acrylicInsertLargeWindows.jpg';
	import adjustingCornerIndowInsert from './images/adjustingCornerIndowInsert.png';
	import manInstallingIndowInsertBayWindow from './images/manInstallingIndowInsertBayWindow.jpg';
	import thermalImagingBeforeAfterIndowInsert from './images/thermalImagingBeforeAfterIndowInsert.png';
	import windowAcrylicInsertNoCondensation from './images/windowAcrylicInsertNoCondensation.jpg';
</script>

<Section>
	<h2>Acrylic vs. Glass: Providing Clarity on the Debate</h2>
	<Image
		alt="Adjusting the corner of an Indow insert: winter scene visible through acrylic insert & glass window."
		src={adjustingCornerIndowInsert}
	/>
	<p>
		You’ve likely heard of plexiglass. What you may not know is that
		“plexiglass” is a word that came into being through the brand Plexiglas®.
		When a company dominates the market for a product, the brand name can become
		the name used for the product itself. The last time you had a paper cut, you
		probably walked to the medicine cabinet for a Band-Aid instead of a bandage.
		Plexiglas® is the brand that dominated the market for acrylic, a plastic
		material that is extremely difficult to break. Even when it does break, it
		does not shatter. Additionally, acrylic has a very high clarity. Hey, that
		sounds like it might be the ideal material for a window insert!
	</p>
	<p>
		Now that we know what acrylic is, let’s talk about why Indow chose acrylic
		over glass for their interior storm window inserts. Performance and
		aesthetic were key in making this decision.
	</p>
	<div class="grid gap-y-2">
		<Image
			alt="Thermal imaging of home window before and after Indow insert: plexiglass keeps in heat"
			src={thermalImagingBeforeAfterIndowInsert}
		/>
		<p class="text-sm">
			Right window fitted with Indow insert, left window has no insert. The
			white thermal imaging of the left window indicates that heat is escaping
			through the window more quickly than the right window with its insert
			keeping heat in.
		</p>
	</div>
</Section>

<Section>
	<h2>Acrylic and Heat Transfer</h2>
	<p>
		Do you mind chatting about science for a moment? We promise it will be quick
		and painless. Heat flows from warmer objects to colder objects. Imagine
		holding an ice cube. Because heat moves from warmer objects to colder ones,
		heat moves from your hand to the ice cube, causing it to melt. If heat moved
		from colder objects to warmer ones, then your hand would warm up while
		holding ice and the ice would remain intact.
	</p>
	<p>
		Glass does not retain heat well. In the winter, your warm inside air meets
		your glass window and because heat moves from warm objects to cold ones, the
		heat moves through the glass and into the cold outside air. You have to run
		your heat continuously in winter because heat is escaping from the warm
		inside to the cold outside. The great news is that acrylic retains heat
		well. Because we use acrylic, Indow inserts transmit heat seven times more
		slowly than regular glass. As a result, heat moves from the insert and
		through the window frame to the cold outside air much more slowly. Warm
		inside air moving out more slowly means you don’t have to run your heating
		system as much to maintain your desired temperature. In turn, you save on
		energy bills. This is a benefit in summer as well. Since Indow inserts
		retain heat well, the hot outside air penetrates into your home much more
		slowly, so you don’t need to run your AC as much to maintain your desired
		temperature.
	</p>
	<Image
		alt="Case Study House #26: acrylic Indow inserts help block draft through the large glass windows"
		src={acrylicInsertLargeWindows}
	/>
</Section>

<Section>
	<h2>Aesthetic and Usability</h2>
	<p>
		Another reason why we chose acrylic over any type of glass insert is because
		of the weight and clarity. The weight of ⅛’’-thick glass is 1.62 pounds per
		square foot. The weight of ⅛’’-thick acrylic is 1 pound per square foot. A
		3×4’ Indow insert will weigh 12 pounds, while a 3×4’ glass insert will weigh
		around 19.44. 3×4’ is considered an average window, but you may have larger
		windows. Using the lighter acrylic for our inserts allows for much easier
		handling during installation and removal. Additionally, acrylic can
		withstand high impact and pressure. On the off chance that it breaks, it
		won’t shatter into sharp shards like glass does. That’s why it’s used on
		submarines and airplanes.
	</p>
	<p>
		It might come as a surprise to hear that glass has a visible light
		transmittance score of less than 100%. However, if you think about driving
		in a car with a clean window that’s rolled up, you have great visibility
		through the window, but if you roll it down the picture outside of the
		window is even clearer. It might also come as a surprise that acrylic has a
		higher visible light transmittance than glass. A single-pane window has a
		transmittance of 90%, while a Standard Grade Indow insert is 92%. Yet
		another reason Indow chose acrylic for our inserts: we want you to feel
		results without seeing the technology behind them.
	</p>
	<Image
		alt="Window fitted with Indow insert next to window with no insert & significant condensation buildup"
		src={windowAcrylicInsertNoCondensation}
	/>
</Section>

<Section>
	<h2>Acrylic and Condensation</h2>
	<p>
		Let’s talk about condensation and if glass or acrylic is better at combating
		it. To do so, we’re going to have to get a bit scientific again. Ready? The
		dew point is the temperature at which air cannot hold any more moisture. And
		that temperature is lower than the air temperature. When air hits a cold
		surface, its temperature drops. When the air reaches its dew point, water
		will form on the cold surface. We already learned that glass does not retain
		heat well, so when it’s cold outside, the glass is also cold. When the
		outside temperature cools the glass to the dew point of the inside air,
		condensation will form on the window when the inside air meets the glass.
		Indow inserts are great at reducing or eliminating condensation because it
		retains heat so well. The cold air will not be able to cool the acrylic to
		the indoor air’s dew point. For a less condensed explanation of this issue
		and how acrylic can eliminate it, read more here about <a
			href="/solutions/prevent-window-condensation"
			>removing window condensation</a
		>.
	</p>
	<Image
		alt="Man installing Indow insert behind blinds in his bay window"
		src={manInstallingIndowInsertBayWindow}
	/>
</Section>

<Section>
	<h2>Other Factors to Consider</h2>
	<p>
		Indow does not recommend our inserts as a window replacement product. They
		should always supplement an existing window. This is because, when compared
		1:1, acrylic is not necessarily better than glass for noise reduction and
		scratch resistance. Indow inserts are designed to be a secondary window
		product, never a primary.
	</p>
	<p>
		To address noise issues, Indow created the Acoustic Grade insert. It is the
		thickest insert offered at ¼’’ and reduces noise coming through your window
		opening by 50-70%*. When your Indow representative considers solutions to
		your noise issues, they want to know how much space you are able to put
		between your window and your insert, the type of noise that is bothering
		you, and the distance from the source of the noise. The bigger the air gap
		between the window and insert you can create, the better noise reduction you
		are going to observe.
	</p>
	<CustomerReview fullName="Nathan V">
		They are great. We live next to a busy street, so noise abatement was our
		primary interest. They have signicantly reduced street noise, and we'd
		definitely recommend these to anyone.
	</CustomerReview>
	<p>
		The Commercial Grade insert comes with an additional one-sided or two-sided
		film that makes it more abrasion resistant. It performs better than
		anti-scratch window film, making it an appropriate solution for sports
		facilities and other highly trafficked areas in which they might be bumped
		much more than in your home. While the Commercial Grade is available to all
		Indow clients, including those with residential projects, the additional
		abrasion-resistant film is not absolutely necessary if you plan to keep your
		inserts in place and you do not have children or pets running out. For those
		who do have energetic children and lively pets, the Commercial Grade is a
		great solution.
	</p>
	<p>
		Much thought and consideration went into the decision to use acrylic panels
		over glass. It was absolutely the right decision. As a result, Indow is able
		to offer a product that reduces heat loss, condensation, and noise and is
		easy and safe to handle. And because acrylic is cheaper than glass, we are
		able to offer it at a fraction of the cost of expensive window replacements.
		Take a look at <a href="/products/indowone-window-inserts"
			>all product types</a
		> to learn about other issues Indow inserts can help you address.
	</p>
	<p>
		Indow is proud to work with local recyclers within Portland for our leftover
		acrylic materials. We also donate materials to <a
			href="https://portland.scrapcreativereuse.org">Scrap</a
		>. Scrap upcycles already manufactured materials for artistic purposes and
		to create new use for these existing items.
	</p>
	<p class="text-sm italic">
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full <a
			href="/noise-disclaimer">Noise Reduction Sheet</a
		> to learn more.
	</p>
</Section>

<Reviews />
